import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { walletService, WalletSummary, WalletTransaction } from '../services/walletService';

// Bonus slabs: when user enters an exact amount, extra bonus is added to wallet
const BONUS_SLABS: Record<number, number> = {
  100: 10,
  200: 15,
  300: 20,
  400: 25,
  500: 30,
  700: 40,
  1000: 50,
  1500: 60,
  2000: 70,
};

const typeLabel: Record<string, { label: string; color: string; icon: string }> = {
  MONEY_ADDED:   { label: 'Money Added',    color: 'text-green-600',  icon: '💰' },
  CASHBACK:      { label: 'Cashback',       color: 'text-purple-600', icon: '🎁' },
  ORDER_PAYMENT: { label: 'Order Payment',  color: 'text-red-500',    icon: '🛍️' },
  REFUND:        { label: 'Refund',         color: 'text-blue-500',   icon: '↩️' },
  ADJUSTMENT:    { label: 'Adjustment',     color: 'text-gray-600',   icon: '⚙️' },
};

const WalletPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'overview' | 'add' | 'history'>('overview');
  const [txPage, setTxPage] = useState(1);
  const TX_PER_PAGE = 10;

  // Add money form
  const [amount, setAmount] = useState('');
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [w, tx] = await Promise.all([
        walletService.getWallet(),
        walletService.getTransactions(),
      ]);
      setWallet(w);
      setTransactions(tx);
    } catch { /* not logged in or network */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    load();
    // If coming back from ClickPay callback with ?recharge=success, show success toast
    const params = new URLSearchParams(location.search);
    if (params.get('recharge') === 'success') {
      const returnTo = params.get('returnTo') || localStorage.getItem('walletReturnTo');
      localStorage.removeItem('walletReturnTo');
      if (returnTo) {
        navigate(returnTo);
        return;
      }
      setTab('overview');
      setAddMsg({ type: 'success', text: 'Wallet recharged successfully! 🎉' });
    }
    // Auto-switch to Add Money tab if coming from checkout
    if (params.get('returnTo')) {
      setTab('add');
    }
  }, [user, navigate, load, location.search]);

  const handleAddMoney = async () => {
    const v = parseFloat(amount);
    if (!v || v < 10) { setAddMsg({ type: 'error', text: 'Minimum recharge amount is ₹10' }); return; }
    setAdding(true);
    setAddMsg(null);
    try {
      // Store returnTo in localStorage so we can redirect back after ClickPay callback
      const params = new URLSearchParams(location.search);
      const returnTo = params.get('returnTo');
      if (returnTo) {
        localStorage.setItem('walletReturnTo', returnTo);
      }
      const data = await walletService.initiateClickPayRecharge(v);
      // Redirect directly to ClickPay payment page
      window.location.href = data.redirectUrl;
    } catch (e: any) {
      setAddMsg({ type: 'error', text: e.message || 'Failed to initiate payment' });
      setAdding(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DAA520]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20 max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">My Wallet</h1>
          <p className="text-gray-500 text-sm mt-1">Add money, earn cashback, pay at checkout</p>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-r from-[#1A1A2E] to-[#E91E63] rounded-2xl p-6 text-white mb-6 shadow-lg">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <p className="text-sm opacity-70 mb-1">Available Balance</p>
              <p className="text-5xl font-bold">₹{wallet?.balance.toFixed(2) ?? '0.00'}</p>
              <p className="text-xs opacity-60 mt-2">Inclusive of all cashback earned</p>
            </div>
            <div className="text-right space-y-2">
              <div className="bg-white/10 rounded-xl px-4 py-2">
                <p className="text-xs opacity-70">Total Cashback</p>
                <p className="text-lg font-bold text-yellow-300">₹{wallet?.totalCashbackEarned.toFixed(2) ?? '0.00'}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-xs opacity-70">Total Added</p>
              <p className="font-bold">₹{wallet?.totalMoneyAdded.toFixed(2) ?? '0.00'}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-xs opacity-70">Total Spent</p>
              <p className="font-bold">₹{wallet?.totalMoneySpent.toFixed(2) ?? '0.00'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {(['overview', 'add', 'history'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                tab === t ? 'border-[#E91E63] text-[#E91E63]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t === 'add' ? 'Add Money' : t === 'history' ? 'Transactions' : 'Overview'}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {tab === 'overview' && (
          <div className="space-y-6">
            {/* Recent transactions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Recent Transactions</h2>
                <button onClick={() => setTab('history')} className="text-sm text-[#E91E63] font-medium">View All</button>
              </div>
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-4xl mb-2">💳</div>
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.slice(0, 5).map(tx => <TransactionRow key={tx.id} tx={tx} />)}
                </div>
              )}
            </div>

            {/* Cashback Offers */}
          </div>
        )}

        {/* ── ADD MONEY TAB ── */}
        {tab === 'add' && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Add Money to Wallet</h2>

              {/* Select amount buttons */}
              <p className="text-xs text-gray-500 mb-2">Select amount</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {Object.entries(BONUS_SLABS).map(([amt, bonus]) => (
                  <button key={amt} onClick={() => setAmount(amt)}
                    className={`py-3 rounded-lg text-sm font-medium border transition-colors ${
                      amount === amt ? 'bg-[#E91E63] text-white border-[#E91E63]' : 'border-gray-200 hover:border-[#E91E63] text-gray-700'
                    }`}>
                    <span className="block">₹{amt}</span>
                    <span className={`text-xs ${amount === amt ? 'text-white/80' : 'text-green-600'}`}>+₹{bonus} bonus</span>
                  </button>
                ))}
              </div>

              {/* Bonus preview when amount is selected */}
              {amount && BONUS_SLABS[parseFloat(amount)] && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">You pay</span>
                    <span className="font-semibold">₹{amount}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>🎉 Extra Bonus</span>
                    <span className="font-semibold">+ ₹{BONUS_SLABS[parseFloat(amount)]}</span>
                  </div>
                  <div className="border-t border-yellow-200 mt-2 pt-2 flex justify-between font-bold">
                    <span className="text-green-700">Total Added to Wallet</span>
                    <span className="text-green-700">₹{parseFloat(amount) + BONUS_SLABS[parseFloat(amount)]}</span>
                  </div>
                </div>
              )}

              {/* Payment method — ClickPay only */}
              <div className="mb-5 p-4 border-2 border-[#DAA520] rounded-xl bg-[#DAA520]/5 flex items-center gap-3">
                <img src="/assets/clickpay-logo.png" alt="ClickPay"
                  className="h-7 object-contain"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Pay via ClickPay</p>
                  <p className="text-xs text-gray-500">UPI / QR Code / Cards</p>
                </div>
                <span className="ml-auto text-xs bg-[#DAA520] text-white px-2 py-0.5 rounded-full font-medium">Selected</span>
              </div>

              {addMsg && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${addMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {addMsg.text}
                </div>
              )}

              <button onClick={handleAddMoney} disabled={adding || !amount}
                className="w-full bg-[#E91E63] hover:bg-[#D81B60] text-white py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {adding ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Redirecting...</>
                ) : (
                  <>Pay ₹{amount || '0'} via ClickPay →</>
                )}
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">You will be redirected to ClickPay to complete payment</p>
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
                  <li>Wallet balance can only be used for purchases on Sasta Fashions.</li>
                  <li>Wallet balance and bonus cashback cannot be transferred or withdrawn to a bank account.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ── HISTORY TAB ── */}
        {tab === 'history' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">All Transactions</h2>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-5xl mb-3">📋</div>
                <p>No transactions yet. Add money to get started!</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {transactions.slice((txPage - 1) * TX_PER_PAGE, txPage * TX_PER_PAGE).map(tx => (
                    <TransactionRow key={tx.id} tx={tx} detailed />
                  ))}
                </div>
                {transactions.length > TX_PER_PAGE && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-400">
                      Showing {(txPage - 1) * TX_PER_PAGE + 1}–{Math.min(txPage * TX_PER_PAGE, transactions.length)} of {transactions.length}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                        ← Prev
                      </button>
                      {Array.from({ length: Math.ceil(transactions.length / TX_PER_PAGE) }, (_, i) => (
                        <button key={i} onClick={() => setTxPage(i + 1)}
                          className={`px-3 py-1.5 text-sm rounded-lg ${txPage === i + 1 ? 'bg-[#E91E63] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}>
                          {i + 1}
                        </button>
                      ))}
                      <button onClick={() => setTxPage(p => Math.min(Math.ceil(transactions.length / TX_PER_PAGE), p + 1))}
                        disabled={txPage === Math.ceil(transactions.length / TX_PER_PAGE)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const TransactionRow: React.FC<{ tx: WalletTransaction; detailed?: boolean }> = ({ tx, detailed }) => {
  const meta = typeLabel[tx.type] ?? { label: tx.type, color: 'text-gray-600', icon: '•' };
  const isCredit = tx.amount > 0;
  const date = new Date(tx.createdAt);

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-lg flex-shrink-0">
        {meta.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{tx.description || meta.label}</p>
        <p className="text-xs text-gray-400">
          {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          {' · '}Ref: {tx.referenceNumber}
        </p>
        {detailed && <p className="text-xs text-gray-400">Balance after: ₹{tx.balanceAfter.toFixed(2)}</p>}
      </div>
      <div className="text-right">
        <p className={`font-bold ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
          {isCredit ? '+' : ''}₹{Math.abs(tx.amount).toFixed(2)}
        </p>
        <span className={`text-xs px-1.5 py-0.5 rounded ${meta.color} bg-gray-100`}>{meta.label}</span>
      </div>
    </div>
  );
};

export default WalletPage;
